import type { AppNotification } from './notification.types';

const WELCOME_TAG = 'adlitics-welcome-v1';
const STORAGE_VERSION = 'v2';
const MAX_STORED = 200;

export type WelcomeOrg = {
    id?: string;
    name?: string;
} | null;

const getUserStorageKey = (userId: string): string =>
    `adlitics_notifications_${STORAGE_VERSION}_${userId}`;

function parseStoredNotifications(raw: string): AppNotification[] {
    return JSON.parse(raw).map((n: AppNotification & { timestamp: string }) => ({
        ...n,
        timestamp: new Date(n.timestamp),
    }));
}

function loadLegacyNotifications(
    userId: string,
    organizationIds: string[]
): AppNotification[] {
    const keys = [
        ...organizationIds.map((id) => `adlitics_notifications_${userId}_${id}`),
        `adlitics_notifications_${userId}_no-org`,
    ];

    const merged: AppNotification[] = [];
    const seen = new Set<string>();

    for (const key of keys) {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            for (const n of parseStoredNotifications(raw)) {
                if (seen.has(n.id)) continue;
                seen.add(n.id);
                merged.push(n);
            }
        } catch {
            // ignora chaves corrompidas
        }
    }

    return merged;
}

/** Carrega notificações do storage atual (v2) ou migra chaves legadas. */
export function loadNotifications(
    userId: string,
    organizationIds: string[]
): AppNotification[] {
    try {
        const saved = localStorage.getItem(getUserStorageKey(userId));
        if (saved) return parseStoredNotifications(saved);
        return loadLegacyNotifications(userId, organizationIds);
    } catch (e) {
        console.error('[notification.storage] Failed to parse notifications', e);
        return [];
    }
}

/** Persiste até MAX_STORED notificações para o usuário. */
export function saveNotifications(userId: string, notifications: AppNotification[]): void {
    localStorage.setItem(
        getUserStorageKey(userId),
        JSON.stringify(notifications.slice(0, MAX_STORED))
    );
}

/** Injeta boas-vindas uma vez por usuário/org. Espera a org (evita cabeçalho "Geral"). */
export function withWelcomeNotification(
    loaded: AppNotification[],
    userId: string,
    org?: WelcomeOrg
): AppNotification[] {
    if (!org?.id) return loaded;

    const welcomeKey = `adlitics_welcome_shown_${userId}_${org.id}`;
    const alreadyForOrg = loaded.some(
        (n) => n.tag === WELCOME_TAG && n.organizationId === org.id
    );
    if (localStorage.getItem(welcomeKey) === '1' && alreadyForOrg) {
        return loaded;
    }

    // Welcome criado antes da org carregar → promove para o nome do tenant
    const orphanIdx = loaded.findIndex(
        (n) => n.tag === WELCOME_TAG && !n.organizationId
    );
    if (orphanIdx >= 0) {
        localStorage.setItem(welcomeKey, '1');
        return loaded.map((n, i) =>
            i === orphanIdx
                ? { ...n, organizationId: org.id, organizationName: org.name }
                : n
        );
    }

    if (alreadyForOrg) {
        localStorage.setItem(welcomeKey, '1');
        return loaded;
    }

    localStorage.setItem(welcomeKey, '1');
    return [{
        id: crypto.randomUUID(),
        title: 'Bem-vindo ao Adlitics',
        message: 'Você receberá alertas de tarefas, mensagens e atualizações da equipe aqui.',
        type: 'info',
        timestamp: new Date(),
        read: false,
        tag: WELCOME_TAG,
        organizationId: org.id,
        organizationName: org.name,
        recipientUserId: userId,
    }, ...loaded];
}
